import { useCallback, useState } from "react"
import PropTypes from "prop-types"
import { Label, Spinner } from "reactstrap"
import { toast } from "react-toastify"
import { uploadImages } from "api/imageApi"

/**
 * Lean multi-file voucher/reference image uploader.
 * Uploads immediately on select and reports back the full list of
 * {id, url, original_name, mime_type} objects via onChange — the parent
 * extracts `.id` for the `image_ids` payload field.
 */
const VoucherImageUpload = ({ images = [], onChange, maxFiles = 5, label = "Voucher / Reference Images" }) => {
  const [uploading, setUploading] = useState(false)

  const handleFileChange = useCallback(
    async e => {
      const files = Array.from(e.target.files || [])
      e.target.value = ""
      if (!files.length) return

      if (images.length + files.length > maxFiles) {
        toast.error(`You can attach up to ${maxFiles} images.`)
        return
      }

      const formData = new FormData()
      files.forEach(file => formData.append("images[]", file))

      setUploading(true)
      try {
        const res = await uploadImages(formData)
        const uploaded = res?.data || []
        onChange?.([...images, ...uploaded])
      } catch (error) {
        toast.error("Failed to upload image(s).")
      } finally {
        setUploading(false)
      }
    },
    [images, maxFiles, onChange]
  )

  const handleRemove = index => {
    onChange?.(images.filter((_, i) => i !== index))
  }

  return (
    <div className="mb-3">
      <Label className="d-block">{label}</Label>
      {images.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-2">
          {images.map((img, index) => (
            <div key={img.id} className="position-relative border rounded p-1" style={{ width: 90 }}>
              {img.mime_type && !img.mime_type.startsWith("image/") ? (
                <div
                  className="d-flex align-items-center justify-content-center bg-light rounded"
                  style={{ height: 70 }}
                >
                  <i className="mdi mdi-file-pdf-box font-size-24 text-danger" />
                </div>
              ) : (
                <img
                  src={img.url}
                  alt={img.original_name || "voucher"}
                  className="rounded"
                  style={{ width: "100%", height: 70, objectFit: "cover" }}
                />
              )}
              <button
                type="button"
                className="btn btn-danger btn-sm position-absolute"
                style={{ top: -8, right: -8, borderRadius: "50%", padding: "2px 6px", lineHeight: 1 }}
                onClick={() => handleRemove(index)}
              >
                <i className="mdi mdi-close" />
              </button>
            </div>
          ))}
        </div>
      )}
      <input
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="form-control"
        onChange={handleFileChange}
        disabled={uploading || images.length >= maxFiles}
      />
      {uploading && (
        <div className="text-muted small mt-1">
          <Spinner size="sm" className="me-1" />
          Uploading...
        </div>
      )}
    </div>
  )
}

VoucherImageUpload.propTypes = {
  images: PropTypes.array,
  onChange: PropTypes.func,
  maxFiles: PropTypes.number,
  label: PropTypes.string,
}

export default VoucherImageUpload
